import React from 'react';

/**
 * The SkillUp mark: an angular S in two halves, with an orange arrow climbing
 * through the gap between them.
 *
 * The construction is the point. The letter is not one continuous stroke — it
 * is an upper half and a lower half, set apart along a diagonal, and the arrow
 * runs up that diagonal corridor. So the arrow is not an ornament parked beside
 * the letter: it *is* the middle bar of the S, and it carries on past the
 * letter and out of the frame. One gesture, read in one go.
 *
 * Drawn as geometry rather than shipped as an image so it stays sharp at any
 * size, needs no network request, and can change colour with its surroundings.
 *
 * Colour is the problem this component solves. The body is the brand navy, and
 * the site's header, hero, footer and intro are all navy — a navy mark on them
 * is invisible. So the body is a prop: `tone="light"` on pale backgrounds,
 * `tone="dark"` on the navy ones, where it turns white. The orange never
 * changes: it reads on white and on navy alike, and it is the part people
 * recognise at a glance.
 *
 * The orange is the site's own #F5820A rather than the #FF8A00 from the design
 * sheet — one orange across buttons, links and the mark, or they quietly argue
 * with each other on every page.
 *
 * Sizes used on the site: 64 (the 404 page), 52 (intro), 40 (header), 32
 * (footer), 16 (the browser tab, through public/favicon.svg — that file carries
 * the same geometry and has to be changed together with this one, as does the
 * share card public/og-image.png).
 */

const NAVY = '#0F2347';
const ORANGE = '#F5820A';

// Both halves of the letter, the arrow's shaft and its head. Named rather than
// inlined so favicon.svg and the share card can be checked against the same
// numbers instead of against a redrawn copy.
const S_UPPER = 'M29 11 H18 L9.5 19.5 L16.5 26.5';
const S_LOWER = 'M19 37 H30 L38.5 28.5 L31.5 21.5';
const S_WIDTH = 7.6;
const ARROW_SHAFT = 'M17 31 L34.9 13.1';
const ARROW_WIDTH = 4.2;
const ARROW_HEAD = '42,6 37.9,16.1 31.9,10.1';

export default function Logo({ size = 40, tone = 'light', title, className = '' }) {
  // On a navy ground the body has to be white or it disappears into it.
  const body = tone === 'dark' ? '#FFFFFF' : NAVY;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* The two halves of the S. Mitred joins and butt ends, not round ones:
          the square cut is what makes the letter read as a built shape rather
          than as a thick pen line. */}
      <path
        d={S_UPPER}
        stroke={body}
        strokeWidth={S_WIDTH}
        strokeLinejoin="miter"
        strokeLinecap="butt"
        strokeMiterlimit="10"
      />
      <path
        d={S_LOWER}
        stroke={body}
        strokeWidth={S_WIDTH}
        strokeLinejoin="miter"
        strokeLinecap="butt"
        strokeMiterlimit="10"
      />
      {/* The arrow. The shaft is deliberately narrower than the corridor it runs
          in, so a band of background stays visible either side of it: touching,
          the orange and the navy merge into one blob at small sizes. */}
      <path
        d={ARROW_SHAFT}
        stroke={ORANGE}
        strokeWidth={ARROW_WIDTH}
        strokeLinecap="butt"
      />
      <polygon points={ARROW_HEAD} fill={ORANGE} />
    </svg>
  );
}

export { NAVY, ORANGE, S_UPPER, S_LOWER, S_WIDTH, ARROW_SHAFT, ARROW_WIDTH, ARROW_HEAD };
